// =============================================================================
// ui/state/filters.tsx — the global filter, minus the date range.
// -----------------------------------------------------------------------------
// The old client's `activeAccountIds` + `activeFilters`. Date lives in
// `range.tsx` because it resolves against a session-aware anchor and every tab
// reads it directly; the two are presented together in one drawer.
//
// ── ⚠ EMPTY MEANS ALL, AND IT IS NOT AN EMPTY RESULT ──
//
// `accounts: []` selects EVERY account, including unassigned trades. It does not
// select none. `selectors/accounts.ts` reads it that way and every consumer must
// too — the inverted reading shows a trader with six funded accounts a blank
// screen, and it looks exactly like "no data".
//
// ── PERSISTED LOCALLY, NEVER SYNCED ──
//
// Same call as the range (see range.tsx): the old client keeps these in local
// state and deliberately does not put them in `user_preferences`. A filter is
// about the sitting, not the account — syncing it means opening the app on a
// phone and finding last Tuesday's account filter silently applied to every
// number on the screen.
//
// ── 🚨 TWO FACETS NAME IDS THAT OUTLIVE THEIR REGISTRY ──
//
// `patterns` holds pattern ids and `tags` holds custom-tag ids. Delete either
// from its registry while it is selected and the id survives in localStorage,
// matches no trade, and empties the entire book — with the drawer showing
// nothing ticked, because the id it would tick no longer resolves to a name.
//
// The old client closes this with ONE choke point: `getActiveFilters()` prunes
// on read and every consumer goes through it, its own comment saying "a dangling
// id can't silently filter every trade out". This client had the same prune
// copied into the drawer only — so the panel looked clean while the Dashboard
// and Trades still filtered on the dead id. **That is D87: where a rule is
// enforced is part of the rule.**
//
// So the provider exposes `storedFacets` — deliberately named for what it is —
// and `useActiveFacets()` is the only thing that returns an appliable selection.
// Reaching for the raw value now looks wrong at the call site instead of reading
// like the obvious field to use.
// =============================================================================

import {
  createContext, useCallback, useContext, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  isAnnotationValue, parseTagRegistry, pruneUnknownPatterns, pruneUnknownTags,
  UNASSIGNED_FILTER_ID, type AccountSelection,
} from '../../selectors/accounts';
import { usePatterns } from './patterns';
import { usePreferences } from './preferences';

const STORAGE_KEY = 'tc.filters';

/**
 * Every facet dimension, in the order the drawer presents them.
 *
 * ⚠ `FilterChips.test.tsx` iterates this list and fails BY NAME on a dimension
 * with no chip. Adding one here without wiring its chip is the defect that test
 * exists for — the Pattern facet shipped exactly that way for one commit, the
 * badge reading "1" while the always-visible bar named nothing.
 */
export const FACET_DIMS = ['instruments', 'tags', 'patterns', 'daysOfWeek', 'annotation'] as const;
export type FacetDim = (typeof FACET_DIMS)[number];

export interface Facets {
  /** Contract ROOTS — `ES`, not `ESU26`. */
  instruments: readonly string[];
  /**
   * Custom-tag ids, into `preferences.customTags`.
   *
   * ⚠ Stale ids are pruned by `useActiveFacets`, never here — see the header.
   * The applied tags themselves ride `trades.extras`, not a column.
   */
  tags: readonly string[];
  /**
   * Pattern ids.
   *
   * ⚠ Stale ids are PRUNED against the catalogue by `useActiveFacets`, not here.
   * The old client's own comment says why: *"a dangling id can't silently filter
   * every trade out"*. Storage outlives the catalogue, so a pattern deleted while
   * selected would otherwise empty the whole book with nothing on screen to
   * explain it.
   */
  patterns: readonly string[];
  /** 0 = Sunday … 6 = Saturday, of the EFFECTIVE trade date. */
  daysOfWeek: readonly number[];
  /**
   * Annotation-completeness values — see `ANNOTATION_OPTS`.
   *
   * ⚠ Unlike tags and patterns these need NO runtime prune: the vocabulary is a
   * fixed list in `domain/facets.ts`, so an unknown value can only arrive from
   * hand-edited storage and is rejected at the parse below.
   */
  annotation: readonly string[];
}

export const NO_FACETS: Facets = {
  instruments: [], tags: [], patterns: [], daysOfWeek: [], annotation: [],
};

const EMPTY_STORED = (): Stored['facets'] => ({
  instruments: [], tags: [], patterns: [], daysOfWeek: [], annotation: [],
});

export interface FiltersState {
  /** ⚠ Empty = ALL accounts. Never "none". */
  accounts: AccountSelection;
  /**
   * ── ⚠ AS STORED, NOT AS APPLIED ──
   *
   * Unvalidated against the pattern catalogue or the tag registry. Use it to
   * WRITE (toggle, clear) and to persist. To FILTER anything, use
   * `useActiveFacets()` — see the header for the book-emptying failure that
   * separates the two.
   */
  storedFacets: Facets;
  setAccounts: (next: AccountSelection) => void;
  toggleAccount: (id: string) => void;
  toggleFacet: (dim: FacetDim, value: string | number) => void;
  clearFacet: (dim: FacetDim) => void;
  /** Accounts → all, every facet → none. Leaves the DATE range alone, as the
   *  old client does: date has its own reset inside its own section. */
  clearAll: () => void;
}

const Ctx = createContext<FiltersState | null>(null);

export function useFilters(): FiltersState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useFilters() outside <FiltersProvider>');
  return v;
}

export interface ActiveFacets {
  /** Pruned against both registries — safe to hand to `applyFacets`. */
  facets: Facets;
  /** The badge on the Filters button: how many facet VALUES are applied. */
  facetCount: number;
  /** Is anything narrowing the view beyond the date scope? */
  anyActive: boolean;
}

/**
 * The only appliable view of the facet selection.
 *
 * ⚠ HOLDS THE STORED VALUE WHILE A REGISTRY IS STILL LOADING. Pruning against a
 * catalogue that has not arrived would discard a real selection on first paint
 * and then quietly widen every number on the screen — the same class of bug as
 * the fallback render in D86. `null` here means "unknown", never "empty".
 */
export function useActiveFacets(): ActiveFacets {
  const { accounts, storedFacets } = useFilters();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();

  // ⚠ `prefs` null → registry UNKNOWN, so no prune. An empty registry is a real
  // state (a user with no custom tags) and pruning against it is correct; a
  // missing one is not, and pruning against that clears the selection.
  const registry = useMemo(
    () => (prefs ? parseTagRegistry(prefs.customTags) : null),
    [prefs],
  );

  return useMemo(() => {
    const pats = patterns
      ? pruneUnknownPatterns(storedFacets.patterns, patterns)
      : storedFacets.patterns;
    const tags = registry ? pruneUnknownTags(storedFacets.tags, registry) : storedFacets.tags;

    // ⚠ Referentially identical to `storedFacets` when nothing was pruned —
    // both prune helpers return their input array in that case — so a memo
    // downstream does not invalidate on every render.
    const facets: Facets =
      pats === storedFacets.patterns && tags === storedFacets.tags
        ? storedFacets
        : { ...storedFacets, patterns: pats, tags };

    const facetCount =
      facets.instruments.length + facets.tags.length + facets.patterns.length
      + facets.daysOfWeek.length + facets.annotation.length;

    return { facets, facetCount, anyActive: accounts.length > 0 || facetCount > 0 };
  }, [accounts, storedFacets, patterns, registry]);
}

interface Stored {
  accounts: string[];
  facets: {
    instruments: string[];
    tags: string[];
    patterns: string[];
    daysOfWeek: number[];
    annotation: string[];
  };
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<Stored>;
    if (!p || typeof p !== 'object') return null;
    // ⚠ Rebuilt field by field and type-checked per element. Storage survives
    // across versions and is user-editable; a non-string sneaking into
    // `instruments` would compare unequal to every root and silently filter the
    // whole book out — which reads as "you have no trades". A dimension the
    // stored blob predates simply arrives empty, which is the un-filtered state.
    const f = (p.facets ?? {}) as Partial<Stored['facets']>;
    const strings = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    return {
      accounts: strings(p.accounts),
      facets: {
        instruments: strings(f.instruments),
        tags: strings(f.tags),
        patterns: strings(f.patterns),
        daysOfWeek: Array.isArray(f.daysOfWeek)
          ? f.daysOfWeek.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
          : [],
        // ⚠ Checked against the VOCABULARY, not just the type. An unknown
        // annotation value matches no predicate, so it would silently filter
        // every trade out — the old client rejects it on read for the same
        // reason, and this is the dimension's only validation point.
        annotation: strings(f.annotation).filter(isAnnotationValue),
      },
    };
  } catch {
    return null;
  }
}

export interface FiltersProviderProps {
  children: ReactNode;
  /** Injected by tests to bypass storage. */
  initial?: { accounts?: AccountSelection; facets?: Partial<Facets> };
}

export function FiltersProvider({ children, initial }: FiltersProviderProps) {
  const [state, setState] = useState<Stored>(() => {
    if (initial) {
      const f = initial.facets ?? {};
      return {
        accounts: [...(initial.accounts ?? [])],
        facets: {
          instruments: [...(f.instruments ?? [])],
          tags: [...(f.tags ?? [])],
          patterns: [...(f.patterns ?? [])],
          daysOfWeek: [...(f.daysOfWeek ?? [])],
          annotation: [...(f.annotation ?? [])],
        },
      };
    }
    return readStored() ?? { accounts: [], facets: EMPTY_STORED() };
  });

  const write = useCallback((next: Stored) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Persisting is a nicety; the filter still applies for this session.
    }
  }, []);

  const setAccounts = useCallback(
    (next: AccountSelection) => write({ ...state, accounts: [...next] }),
    [state, write],
  );

  const toggleAccount = useCallback((id: string) => {
    const has = state.accounts.includes(id);
    write({ ...state, accounts: has ? state.accounts.filter((x) => x !== id) : [...state.accounts, id] });
  }, [state, write]);

  const toggleFacet = useCallback((dim: FacetDim, value: string | number) => {
    const cur = state.facets[dim] as readonly (string | number)[];
    const has = cur.includes(value);
    const next = has ? cur.filter((v) => v !== value) : [...cur, value];
    write({ ...state, facets: { ...state.facets, [dim]: next } as Stored['facets'] });
  }, [state, write]);

  const clearFacet = useCallback((dim: FacetDim) => {
    write({ ...state, facets: { ...state.facets, [dim]: [] } as Stored['facets'] });
  }, [state, write]);

  const clearAll = useCallback(() => {
    write({ accounts: [], facets: EMPTY_STORED() });
  }, [write]);

  const value = useMemo<FiltersState>(() => ({
    accounts: state.accounts,
    storedFacets: state.facets,
    setAccounts, toggleAccount, toggleFacet, clearFacet, clearAll,
  }), [state, setAccounts, toggleAccount, toggleFacet, clearFacet, clearAll]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export { UNASSIGNED_FILTER_ID };
